import { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Image as ImageIcon, Video, Upload, Link, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getGallery, createGalleryItem, deleteGalleryItem, uploadFile } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [removePromptOpen, setRemovePromptOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [draft, setDraft] = useState({ type: 'image', url: '', title: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const pickerRef = useRef(null);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const res = await getGallery();
      setItems(res.data);
    } catch (err) {
      toast.error('Не удалось загрузить галерею');
    } finally {
      setBusy(false);
    }
  };

  const startCreate = (type) => {
    setDraft({ type, url: '', title: '' });
    setUploadMode(type === 'image' ? 'file' : 'url');
    setEditorOpen(true);
  };

  const askDelete = (item) => {
    setActiveRow(item);
    setRemovePromptOpen(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Файл слишком большой. Максимум 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const res = await uploadFile(file);
      const fullUrl = `${API_URL}${res.data.url}`;
      setDraft({ ...draft, url: fullUrl });
      toast.success('Файл загружен');
    } catch (err) {
      toast.error('Не удалось загрузить файл');
    } finally {
      setIsUploading(false);
    }
  };

  const persistDraft = async () => {
    if (!draft.url) {
      toast.error('Загрузите фото или укажите URL');
      return;
    }

    try {
      await createGalleryItem(draft);
      toast.success('Добавлено');
      setEditorOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось добавить');
    }
  };

  const performDelete = async () => {
    if (!activeRow) return;

    try {
      await deleteGalleryItem(activeRow.id);
      toast.success('Удалено');
      setRemovePromptOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось удалить');
    }
  };

  const images = items.filter(i => i.type === 'image');
  const videos = items.filter(i => i.type === 'video');

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Галерея</h1>
        <p className="text-slate-500">Фото и видео для лендинга</p>
      </div>

      <Tabs defaultValue="images">
        <TabsList>
          <TabsTrigger value="images" className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Фото ({images.length})
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Видео ({videos.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="images" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => startCreate('image')} data-testid="add-image-button">
              <Plus className="w-4 h-4 mr-2" />
              Добавить фото
            </Button>
          </div>

          {images.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ImageIcon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Нет фотографий</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((item) => (
                <div key={item.id} className="relative group" data-testid={`gallery-image-${item.id}`}>
                  <div className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-100">
                    <img
                      src={item.url}
                      alt={item.title || 'Фото'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => askDelete(item)}
                    className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-image-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="videos" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => startCreate('video')} data-testid="add-video-button">
              <Plus className="w-4 h-4 mr-2" />
              Добавить видео
            </Button>
          </div>

          {videos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Нет видео</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((item) => (
                <Card key={item.id} data-testid={`gallery-video-${item.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Video className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{item.title || 'Видео'}</p>
                        <p className="text-sm text-slate-500 truncate">{item.url}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => askDelete(item)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-video-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {draft.type === 'image' ? 'Добавить фото' : 'Добавить видео'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {draft.type === 'image' && (
              <div className="flex gap-2 mb-4">
                <Button
                  variant={uploadMode === 'file' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUploadMode('file')}
                  className="flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Загрузить
                </Button>
                <Button
                  variant={uploadMode === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUploadMode('url')}
                  className="flex-1"
                >
                  <Link className="w-4 h-4 mr-2" />
                  По ссылке
                </Button>
              </div>
            )}

            {draft.type === 'image' && uploadMode === 'file' ? (
              <div className="space-y-4">
                <input
                  type="file"
                  ref={pickerRef}
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                
                {draft.url ? (
                  <div className="space-y-2">
                    <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={draft.url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => pickerRef.current?.click()}
                      disabled={isUploading}
                    >
                      Заменить фото
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => !isUploading && pickerRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-slate-300 transition-colors"
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-2" />
                        <p className="text-sm text-slate-500">Загрузка...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 font-medium">Нажмите для выбора файла</p>
                        <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF, WebP до 5MB</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>URL *</Label>
                <Input
                  value={draft.url}
                  onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                  placeholder={draft.type === 'image' ? 'https://example.com/photo.jpg' : 'https://youtube.com/embed/...'}
                  data-testid="gallery-url-input"
                />
                <p className="text-xs text-slate-500">
                  {draft.type === 'image'
                    ? 'Прямая ссылка на изображение'
                    : 'Ссылка для встраивания (embed URL от YouTube/Vimeo)'}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Название (опционально)</Label>
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Описание"
                data-testid="gallery-title-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Отмена
            </Button>
            <Button onClick={persistDraft} disabled={!draft.url || isUploading} data-testid="save-gallery-button">
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={removePromptOpen} onOpenChange={setRemovePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить этот элемент? Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-gallery"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
